import { useState } from 'react';
import { PageHeader } from '../components/ui';
import { useSession } from '../lib/session';
import { AuthorizePresetsTab } from './AuthorizePresets';
import { DevicePresetsTab } from './DevicePresets';

/**
 * Both kinds of preset on one page (operator's call, 2026-08-25), replacing two
 * separate nav entries.
 *
 * They were adjacent in the menu and read as near-duplicates, which is exactly
 * the confusion worth removing: an operator choosing between them is choosing
 * WHICH SIDE gets told, not which feature to use. Authorise presets are what
 * the OLT is told when an ONU is brought into service; device presets are what
 * the ONU itself is told afterwards. Side by side under one title, the split is
 * the first thing on screen instead of something to infer from two menu labels.
 */
type Tab = 'authorize' | 'device';

const TABS: Array<{ id: Tab; label: string }> = [
  // Authorise comes first because it happens first: a subscriber is authorised
  // onto the chassis before anything is written to their router.
  { id: 'authorize', label: 'Authorise presets' },
  { id: 'device', label: 'Device presets' },
];

export function PresetsPage() {
  const [tab, setTab] = useState<Tab>('authorize');
  const { can } = useSession();

  // The same capability guards both routes, so a viewer sees the page and the
  // tabs and simply cannot create — which is what the tab bodies already do.
  if (!can('olt.read')) return null;

  return (
    <>
      <PageHeader
        title="Presets"
        description="Saved answers, applied in one tap. Authorise presets are what the OLT is told when an ONU comes into service; device presets are what the ONU itself is told."
      />

      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-line">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setTab(entry.id)}
            className={
              tab === entry.id
                ? 'border-b-2 border-primary px-4 py-2.5 text-sm font-medium text-primary'
                : 'border-b-2 border-transparent px-4 py-2.5 text-sm text-muted hover:text-ink'
            }
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'authorize' ? <AuthorizePresetsTab /> : <DevicePresetsTab />}
    </>
  );
}
